import OpenAI from 'openai';

const ES_PROVIDERS_API = import.meta.env.VITE_ES_PROVIDERS_API;

// Inicializar OpenAI con la API key desde variables de entorno
const OPENAI_API_KEY = import.meta.env.VITE_OPENAI_API_KEY;

if (!OPENAI_API_KEY) {
  console.warn('⚠️ VITE_OPENAI_API_KEY no está definida en las variables de entorno');
}

const openai = new OpenAI({
  apiKey: OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export async function useGPTToTranslate({ template, query, data, glossary }) {
  try {
    const completion = await openai.chat.completions.create({
      messages: [
        { role: "system", content: template },
        { role: "user", content: query },
        {
          role: "assistant",
          content:
            "Sure, give me the glossary and then I will use it to translate the information"
        },
        { role: "user", content: glossary },
        {
          role: "assistant",
          content:
            "I will now use the glossary to translate the information, so please send me the information"
        },
        { role: "user", content: data }
      ],
      model: "gpt-4o",
      max_tokens: 16384,
      temperature: 0
    });

    const responseContent = completion.choices[0]?.message.content;
    const finishReason = completion.choices[0]?.finish_reason;

    // Detectar si la respuesta fue truncada
    if (finishReason === "length") {
      console.warn("⚠️ Translation response was truncated due to token limit");
      
      // Intentar validar si el JSON está completo
      if (responseContent) {
        const cleanedContent = responseContent
          .replace(/^```json\s*\n?/i, "")
          .replace(/\n?```\s*$/i, "")
          .trim();
        
        try {
          JSON.parse(cleanedContent);
        } catch (parseError) {
          throw new Error(
            `Translation response was truncated and JSON is incomplete. ` +
            `JSON parsing failed: ${parseError.message}. ` +
            `Response length: ${responseContent.length} characters. ` +
            `The content may be too long. Consider splitting the translation into smaller parts.`
          );
        }
      } else {
        throw new Error(
          "Translation response was truncated and no content was returned."
        );
      }
    }

    // Validar que el JSON esté completo (incluso si no fue truncado)
    if (responseContent) {
      const cleanedContent = responseContent
        .replace(/^```json\s*\n?/i, "")
        .replace(/\n?```\s*$/i, "")
        .trim();
      
      try {
        JSON.parse(cleanedContent);
      } catch (parseError) {
        throw new Error(
          `Invalid JSON in translation response: ${parseError.message}`
        );
      }
    }

    return responseContent;
  } catch (error) {
    // Mejorar mensajes de error
    if (error.message.includes("truncated") || error.message.includes("JSON")) {
      throw error;
    }
    throw new Error(`Translation failed: ${error.message}`);
  }
}

export async function useTranslateText({ template, query, data, ...rest }) {
  try {
    // Usar la función directamente en lugar de hacer fetch al endpoint
    const response = await useGPTToTranslate({
      template,
      query,
      data,
      glossary: rest.glossary || ''
    });
    
    // Retornar un objeto que simula un Response de fetch para mantener compatibilidad
    return {
      ok: true,
      status: 200,
      statusText: 'OK',
      json: async () => ({ response }),
      text: async () => JSON.stringify({ response })
    };
  } catch (error) {
    // Retornar un objeto que simula un Response de error
    return {
      ok: false,
      status: 500,
      statusText: 'Internal Server Error',
      json: async () => ({ error: error.message }),
      text: async () => error.message
    };
  }
}

export default async function useExtractInfo({ query, infoToSearch, template, stream = true }) {
  const response = await fetch(`${ES_PROVIDERS_API}/ai/gpt/extract-info-and-create-doc`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query,
      data: infoToSearch,
      template,
      stream
    })
  });
  return response;
}
